/**
 * enginePreProcess_Merge.ts - MergeTemplates implementation for EnginePreProcess
 * Pure ES Module version
 */

import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { JsonMergeUtil } from '../../base/engine/jsonMergeUtil.js';
import { DynamicResults } from '../../model/dynamicResults.js';
import { MergeResult } from '../../model/mergeResult.js';
import { TransientStore } from '../../registry/transientStore.js';

import {
    createFlowStack,
    getMainTemplate,
    mergeMainTemplateJson,
    buildOperationsMetadata
} from './enginePreProcess_Helper.js';

import type { EnginePreProcess } from './enginePreProcess.js';
import type { PhaseTiming } from '../../base/timing/phaseTiming.js';
import { TimingPhase } from '../../base/timing/timingPhase.js';
import { JsonObject, JsonValue } from '../../app/json/jsonValue.js';
import { CommonUtil } from '../../common/commonUtil.js';
import type { TemplateOperation } from '../../model/templateOperation.js';
import type { InheritanceOperation } from '../../model/inheritanceOperation.js';
import { JsonUtility } from '../../app/jsonUtility.js';
import { SensitiveArtifactRedactor } from '../../common/sensitiveArtifactRedactor.js';
import { SensitiveDataRedactor } from '@arshu/common';

/**
 * Merges templates using ONLY pre-parsed data structures from PreprocessedTemplate.
 * Uses Placeholders and SlottedTemplates lists - NO IndexOf parsing.
 * Uses the loader provided at construction time.
 * Returns MergeResult with: (appFile, componentHtml, operations, dynamicData, dynamicView, dynamicAction)
 */
export function mergeTemplates(this: EnginePreProcess, appSite: string, appView: string | null, genOperationJson: boolean, genComponentHtml: boolean, contextJson: JsonObject | null, timing: PhaseTiming, configJson: JsonObject | null = null, actionMode: boolean = false, clientMode: boolean = false): MergeResult {
    const engine = this;
    const sensitiveScope = SensitiveDataRedactor.beginScope(JsonUtility.getSensitiveStringValues(configJson));

    Logger.flow(`MergeTemplates: enter appSite=${appSite} appView=${appView || ''} actionMode=${actionMode}`, "PreProcess.MergeLifecycle");
    // AppSite-triggered resolution (loader-owned): an IAppSite handler may redefine the AppSite/AppView
    // before any appfile/template resolution. Unchanged when none applies.
    const { appSiteResolved, navigationTrace, appFileResult, mergeContextJson, mergeConfigJson } = timing.measure(TimingPhase.Resolve, () => {
        const appSiteResolved = engine._loader!.applyDynamicAppSiteHandlers(appSite, appView, contextJson ?? new JsonObject(), configJson, timing);
        appSite = appSiteResolved.appSite;
        appView = appSiteResolved.appView;
        // Drain the per-merge navigation trace (ordered IAppSite hops) accumulated at the merge top.
        const navigationTrace = engine._loader!.getAndClearNavigationTrace();
        // Resolve appFile and validate appView via loader
        const appFileResult = engine._loader!.getAppFile(appSite, appView || '');
        appView = appFileResult.validatedAppView;
        Logger.flow(`MergeTemplates: appFile=${appFileResult.appFile}`, "PreProcess.MergeLifecycle");
        const { mergeContextJson, mergeConfigJson } = engine.prepareProtectedMergeContext(appSite, appView, appFileResult.appFile, contextJson, configJson);
        return { appSiteResolved, navigationTrace, appFileResult, mergeContextJson, mergeConfigJson };
    });
    const appFile: string = appFileResult.appFile;

    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
        Logger.debug(`MergeTemplates called: appSite=${appSite}, appFile=${appFile}, appView=${appView ?? 'null'}`, 'EnginePreProcess');
    }

    // Create fresh DynamicResults and pass to loader for clean state per merge
    const dynamicResults = timing.measure(TimingPhase.Bind, () => engine.initializeDynamicMergeState(appSite, appFile, appView, actionMode));

    // Create componentHtml map for intermediate outputs
    const componentHtml = new JsonObject();

    try {
        // Create context stack for inheritance resolution (supports multi-parent scenarios)
        const flowStack = createFlowStack(appFile);

        // Get main template using helper
        const { mainTemplate, mainJson, resolvedTemplateName, resolvedAppSite, mainLatePlaceholders } =
            getMainTemplate(engine, appSite, appFile, appView, flowStack, timing, mergeConfigJson, clientMode);

        if (!mainTemplate || !mainTemplate.originalContent) {
            Logger.flow(`MergeTemplates: mainTemplate=missing`, "PreProcess.MergeLifecycle");
            Logger.warn(`Main template not found for appSite=${appSite}, appFile=${appFile}`, 'EnginePreProcess');
            componentHtml.set(appFile, JsonValue.string(engine._loader!.defaultReplaceHtml));
            const emptyTemplateKey = `${appSite}_${appFile}`;
            const emptyOps = buildOperationsMetadata(genOperationJson, appSite, appFile, appView, emptyTemplateKey, [], new Array<InheritanceOperation>(), timing);
            const emptyResult = new MergeResult(appFile, componentHtml, emptyOps);
            emptyResult.resolvedAppSite = appSiteResolved.appSite;
            emptyResult.resolvedAppView = appSiteResolved.appView;
            emptyResult.navigationTrace = navigationTrace;
            timing.measure(TimingPhase.Assemble, () => SensitiveArtifactRedactor.redact(emptyResult));
            return emptyResult;
        }

        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`Main template found, html size: ${mainTemplate.originalContent.length}`, 'EnginePreProcess');
        }

        // Create operations list for tracking
        const operations: TemplateOperation[] = [];
        // Use appFile for template key when resolved name is the default "NotFound" placeholder
        // This matches Normal engine behavior which always uses appFile for the main template key
        const effectiveTemplateName = resolvedTemplateName === engine._loader!.defaultReplaceTemplateName ? appFile : resolvedTemplateName;
        const templateKey = `${resolvedAppSite}_${effectiveTemplateName}`;

        // Start with original content
        // DynamicView handlers are applied by getMainTemplate (Area 7: explicit engine call)
        let contentHtml = mainTemplate.originalContent;
        Logger.flow(`MergeTemplates: mainTemplate=found resolvedName=${resolvedTemplateName || ''} slots=${mainTemplate.slottedTemplates.length} placeholders=${mainTemplate.placeholders.length}`, "PreProcess.MergeLifecycle");

        // Merge main template with JSON using helper
        Logger.flow(`MergeTemplates: mainMerge=enter templateKey=${templateKey}`, "PreProcess.MergeLifecycle");
        contentHtml = mergeMainTemplateJson(engine, mainTemplate, mainJson, contentHtml, templateKey, appFile, operations, mergeContextJson, mergeConfigJson, timing);

        if (Logger.getLogLevel() <= LogLevel.DEBUG && mainJson) {
            Logger.debug(`After main JSON merge: ${contentHtml.length} chars`, 'EnginePreProcess');
        }

        // Process template using pre-parsed structures (NO IndexOf)
        Logger.flow(`MergeTemplates: ProcessTemplateContent=enter late=${mainLatePlaceholders.length}`, "PreProcess.MergeLifecycle");
        contentHtml = engine._processTemplateContent(
            contentHtml,
            mainTemplate,
            appSite,
            appFile,
            appView,
            templateKey,
            operations,
            0,
            componentHtml,
            genComponentHtml,
            timing,
            mergeContextJson,
            flowStack,
            mergeConfigJson,
            mainLatePlaceholders,
            clientMode
        );

        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`MergeTemplates complete: output size=${contentHtml.length}`, 'EnginePreProcess');
        }

        const result = timing.measure(TimingPhase.Assemble, () => {
            // Unescape data values that were protected from placeholder re-interpretation
            contentHtml = JsonMergeUtil.unescapeDataValues(contentHtml);
            engine.unescapeProtectedDataValues(componentHtml);

            Logger.flow(`MergeTemplates: exit components=${componentHtml.size + 1}`, "PreProcess.MergeLifecycle");
            // Store final HTML in componentHtml under appFile key
            componentHtml.set(appFile, JsonValue.string(contentHtml));

            // Server generation (§2B.4): join the Embedded client-data islands into the FINAL page HTML as a
            // SEPARATE DataHtml — one island per component keyed by component name, sourced from the raw the
            // preparers recorded — inserted before the last </body> (or appended for a fragment), AFTER the unescape
            // pass so the raw is never rescanned/mutated. No-op when no Embedded raw was recorded.
            componentHtml.set(appFile, JsonValue.string(
                CommonUtil.appendClientDataIslands(componentHtml.getValue(appFile)?.asString() ?? '', dynamicResults.dynamicClientDataResults)));

            // Collect inheritance operations from loader
            const inheritanceOps = engine._loader!.getAndClearInheritanceOperations();

            // Build operations metadata using helper
            const opsMetadata = buildOperationsMetadata(genOperationJson, appSite, appFile, appView, templateKey, operations, inheritanceOps, timing);

            // Get dynamic data, dynamic view, and dynamic action results from engine-owned DynamicResults
            const { dynamicData, dynamicView, dynamicAction, dynamicClientData, dynamicClient } = engine.collectDynamicHandlerResults(dynamicResults, timing);

            const assembled = new MergeResult(appFile, componentHtml, opsMetadata, dynamicData, dynamicView, dynamicAction);
            assembled.dynamicClientData = dynamicClientData;
            assembled.dynamicClient = dynamicClient;
            assembled.resolvedAppSite = appSiteResolved.appSite;
            assembled.resolvedAppView = appSiteResolved.appView;
            assembled.navigationTrace = navigationTrace;
            return assembled;
        });
        timing.measure(TimingPhase.Assemble, () => SensitiveArtifactRedactor.redact(result));
        return result;
    } catch (ex) {
        Logger.error(`Merge failed: ${(ex as Error).message}`, 'EnginePreProcess');
        Logger.fallback("F-ENG1", `merge exception surfaced: appSite=${appSite} appFile=${appFile} ex=${(ex as Error).message}`);
        throw ex;
    } finally {
        sensitiveScope.dispose();
        // Always clear action mode, even on early returns and exceptions
        engine._loader!.setActionMode(false);
    }
}

export function prepareProtectedMergeContext(this: EnginePreProcess, appSite: string, appView: string | null, appFile: string, contextJson: JsonObject | null, configJson: JsonObject | null): { mergeContextJson: JsonObject | null; mergeConfigJson: JsonObject | null } {
    return {
        mergeContextJson: contextJson ? this.createProtectedMergeContext(appSite, appView, appFile, 'PreProcess', contextJson) : null,
        mergeConfigJson: this.createProtectedMergeConfig(configJson)
    };
}

export function initializeDynamicMergeState(this: EnginePreProcess, appSite: string, appFile: string, appView: string | null, actionMode: boolean): DynamicResults {
    const dynamicResults = new DynamicResults();
    this._loader!.setDynamicResults(dynamicResults);
    this._loader!.setActionMode(actionMode);
    const transientStore = new TransientStore();
    this._loader!.setTransientStore(transientStore);
    return dynamicResults;
}

export function unescapeProtectedDataValues(this: EnginePreProcess, componentHtml: JsonObject): void {
    for (const [key, value] of componentHtml) {
        componentHtml.set(key, JsonValue.string(JsonMergeUtil.unescapeDataValues(value.asString() || '')));
    }
}

export function collectDynamicHandlerResults(this: EnginePreProcess, dynamicResults: DynamicResults, timing: PhaseTiming): { dynamicData: JsonObject; dynamicView: JsonObject; dynamicAction: JsonObject; dynamicClientData: JsonObject; dynamicClient: JsonObject } {
    return timing.measure(TimingPhase.Assemble, () => {
    const dynamicData = dynamicResults.dynamicDataResults;
    const dynamicView = dynamicResults.dynamicViewResults;
    const dynamicAction = dynamicResults.dynamicActionResults;
    const dynamicClientData = dynamicResults.dynamicClientDataResults;
    const dynamicClient = dynamicResults.dynamicClientResults;

    // Unescape data values in dynamic view results
    for (const [key, value] of dynamicView) {
        dynamicView.set(key, JsonValue.string(JsonMergeUtil.unescapeDataValues(value.asString() || '')));
    }

    return { dynamicData, dynamicView, dynamicAction, dynamicClientData, dynamicClient };
    });
}
